import { useEffect, useState } from 'react';

export default function QuestionFormModal({ question, isNew, onSave, onClose }) {
  const optionsForType = (type) => {
    if (type === 'true-false') return ['True', 'False'];
    if (type === 'task') return ['Submit Solution & Image'];
    if (type === 'discussion') return [];
    if (type === 'fill-blank') return [];
    if (type === 'ordering') return ['', '', ''];
    return ['', '', '', ''];
  };

  const [draft, setDraft] = useState({
    id: question?.id || `q-${Date.now()}`,
    text: question?.questionText || question?.text || '',
    type: question?.type || 'mcq',
    imageUrl: question?.imageUrl || '',
    options: question?.options?.length ? question.options : optionsForType(question?.type),
    correctAnswer: question?.correctAnswer ?? 0,
    // 'multi-select' only: indices of every correct option.
    correctAnswers: question?.correctAnswers?.length ? question.correctAnswers : [],
    // 'fill-blank' only: any one of these counts as correct.
    acceptedAnswers: question?.acceptedAnswers?.length ? question.acceptedAnswers : [''],
    explanation: question?.explanation || '',
  });

  // Sync when question prop changes (important for editing)
  useEffect(() => {
    if (question) {
      setDraft({
        id: question.id || `q-${Date.now()}`,
        text: question.questionText || question.text || '',
        type: question.type || 'mcq',
        imageUrl: question.imageUrl || '',
        options: question.options?.length ? question.options : optionsForType(question.type),
        correctAnswer: question.correctAnswer ?? 0,
        correctAnswers: question.correctAnswers?.length ? question.correctAnswers : [],
        acceptedAnswers: question.acceptedAnswers?.length ? question.acceptedAnswers : [''],
        explanation: question.explanation || '',
      });
    }
  }, [question]);

  const handleTypeChange = (type) => {
    setDraft(prev => ({
      ...prev,
      type,
      options: optionsForType(type),
      correctAnswer: 0,
      correctAnswers: [],
      acceptedAnswers: type === 'fill-blank' ? (prev.type === 'fill-blank' ? prev.acceptedAnswers : ['']) : prev.acceptedAnswers,
    }));
  };

  const toggleMultiCorrect = (idx) => {
    setDraft(prev => {
      const has = prev.correctAnswers.includes(idx);
      const correctAnswers = has
        ? prev.correctAnswers.filter(i => i !== idx)
        : [...prev.correctAnswers, idx].sort((a, b) => a - b);
      return { ...prev, correctAnswers };
    });
  };

  const updateAcceptedAnswer = (idx, value) => {
    const acceptedAnswers = [...draft.acceptedAnswers];
    acceptedAnswers[idx] = value;
    setDraft(prev => ({ ...prev, acceptedAnswers }));
  };

  const addAcceptedAnswer = () => setDraft(prev => ({ ...prev, acceptedAnswers: [...prev.acceptedAnswers, ''] }));

  const removeAcceptedAnswer = (idx) => setDraft(prev => ({
    ...prev,
    acceptedAnswers: prev.acceptedAnswers.filter((_, i) => i !== idx)
  }));

  const addOrderingItem = () => setDraft(prev => ({ ...prev, options: [...prev.options, ''] }));

  const removeOrderingItem = (idx) => setDraft(prev => ({
    ...prev,
    options: prev.options.filter((_, i) => i !== idx)
  }));

  const moveOrderingItem = (idx, dir) => {
    setDraft(prev => {
      const options = [...prev.options];
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= options.length) return prev;
      [options[idx], options[newIdx]] = [options[newIdx], options[idx]];
      return { ...prev, options };
    });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Image must be under 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setDraft(prev => ({ ...prev, imageUrl: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const updateOption = (idx, value) => {
    const options = [...draft.options];
    options[idx] = value;
    setDraft(prev => ({ ...prev, options }));
  };

  const handleSave = () => {
    if (!draft.text.trim()) return;

    // Convert to backend format. Discussion questions are genuinely
    // open-ended -- no options and no correct answer to force -- so leave
    // both empty/undefined rather than defaulting to 0 like the other types.
    // Fill-blank has no options at all; multi-select and ordering don't use
    // the single `correctAnswer` index (they have their own fields below).
    const questionForBackend = {
      questionText: draft.text.trim(),
      type: draft.type,
      imageUrl: draft.imageUrl || '',
      options:
        draft.type === 'task' ? ['Task Completed & Submitted'] :
        draft.type === 'discussion' ? [] :
        draft.type === 'fill-blank' ? [] :
        draft.options,
      correctAnswer:
        draft.type === 'task' ? 0 :
        ['discussion', 'fill-blank', 'multi-select', 'ordering'].includes(draft.type) ? undefined :
        Number(draft.correctAnswer),
      correctAnswers: draft.type === 'multi-select' ? draft.correctAnswers : undefined,
      acceptedAnswers: draft.type === 'fill-blank'
        ? draft.acceptedAnswers.map(a => a.trim()).filter(Boolean)
        : undefined,
      explanation: draft.explanation.trim(),
    };

    onSave(questionForBackend);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl border border-slate-100 flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h3 className="font-bold text-slate-900">{isNew ? 'Add Question or Task' : 'Edit Question or Task'}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 cursor-pointer rounded-lg hover:bg-slate-100">
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {/* Question Type */}
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-2">Item Type</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { id: 'mcq', label: 'Multiple Choice', icon: 'ri-list-check' },
                { id: 'true-false', label: 'True / False', icon: 'ri-toggle-line' },
                { id: 'multi-select', label: 'Multi-Select', icon: 'ri-checkbox-multiple-line' },
                { id: 'fill-blank', label: 'Fill in Blank', icon: 'ri-input-method-line' },
                { id: 'ordering', label: 'Ordering', icon: 'ri-sort-asc' },
                { id: 'task', label: 'Practical Task', icon: 'ri-task-line' },
                { id: 'discussion', label: 'Discussion', icon: 'ri-discuss-line' }
              ].map(type => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => handleTypeChange(type.id)}
                  className={`flex flex-col items-center justify-center gap-1.5 p-2.5 rounded-xl text-xs font-semibold border transition-all cursor-pointer ${
                    draft.type === type.id
                      ? 'bg-teal-600 text-white border-teal-600 shadow-sm'
                      : 'text-slate-600 border-slate-200 hover:border-teal-300 bg-white'
                  }`}
                >
                  <i className={`${type.icon} text-base`} />
                  <span>{type.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Question / Task Prompt Text */}
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1.5">
              {draft.type === 'task' ? 'Task / Practical Assignment Prompt *' : draft.type === 'discussion' ? 'Discussion / Reflection Question *' : 'Question *'}
            </label>
            <textarea
              value={draft.text}
              onChange={e => setDraft(prev => ({ ...prev, text: e.target.value }))}
              placeholder={draft.type === 'task' ? 'e.g. Build a flexbox navbar with a CTA button and submit a screenshot of your output...' : draft.type === 'discussion' ? 'e.g. How does this week\'s topic apply to your own life right now?' : 'Enter your question here...'}
              rows={3}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-500 resize-none"
            />
          </div>

          {/* Optional Reference Image for Question / Task */}
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1.5">
              Reference Image / Diagram / Prompt Asset (Optional)
            </label>
            {draft.imageUrl ? (
              <div className="relative rounded-xl border border-slate-200 overflow-hidden mb-2 bg-slate-50">
                <img src={draft.imageUrl} alt="Reference" className="w-full h-36 object-contain" />
                <button
                  type="button"
                  onClick={() => setDraft(prev => ({ ...prev, imageUrl: '' }))}
                  className="absolute top-2 right-2 px-2.5 py-1 bg-rose-600 text-white text-xs font-semibold rounded-lg hover:bg-rose-700 shadow-md flex items-center gap-1"
                >
                  <i className="ri-delete-bin-line" /> Remove
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <label className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-slate-200 hover:border-teal-400 rounded-xl cursor-pointer bg-slate-50/50 hover:bg-teal-50/20 transition-all">
                  <i className="ri-image-add-line text-2xl text-teal-600 mb-1" />
                  <span className="text-xs font-medium text-slate-700">Click to upload reference image or diagram</span>
                  <span className="text-[11px] text-slate-400">PNG, JPG, WebP up to 5MB</span>
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400">or URL:</span>
                  <input
                    type="url"
                    placeholder="https://..."
                    value={draft.imageUrl}
                    onChange={e => setDraft(prev => ({ ...prev, imageUrl: e.target.value }))}
                    className="flex-1 px-3 py-1.5 border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-teal-500"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Options for MCQ / True-False (single correct answer) */}
          {(draft.type === 'mcq' || draft.type === 'true-false') && (
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-2">
                {draft.type === 'true-false' ? 'Options' : 'Answer Options *'}
              </label>
              <div className="space-y-2">
                {draft.options.map((opt, idx) => (
                  <div key={idx} className="flex items-center gap-2.5">
                    <button
                      type="button"
                      onClick={() => setDraft(prev => ({ ...prev, correctAnswer: idx }))}
                      className={`w-6 h-6 flex items-center justify-center rounded-full border-2 flex-shrink-0 cursor-pointer transition-colors ${
                        draft.correctAnswer === idx
                          ? 'border-teal-500 bg-teal-500'
                          : 'border-slate-300 hover:border-teal-300'
                      }`}
                    >
                      {draft.correctAnswer === idx && <i className="ri-check-line text-white text-xs" />}
                    </button>
                    <input
                      type="text"
                      value={opt}
                      onChange={e => updateOption(idx, e.target.value)}
                      disabled={draft.type === 'true-false'}
                      placeholder={`Option ${String.fromCharCode(65 + idx)}`}
                      className={`flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none transition-colors ${
                        draft.type === 'true-false'
                          ? 'bg-slate-50 text-slate-500 border-slate-200'
                          : draft.correctAnswer === idx
                          ? 'border-teal-300 bg-teal-50 focus:border-teal-500'
                          : 'border-slate-200 focus:border-teal-500'
                      }`}
                    />
                    {draft.correctAnswer === idx && (
                      <span className="text-xs text-teal-600 font-semibold whitespace-nowrap">Correct</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-2">Click the circle to mark the correct answer</p>
            </div>
          )}

          {/* Options for Multi-Select (several correct answers) */}
          {draft.type === 'multi-select' && (
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-2">Answer Options *</label>
              <div className="space-y-2">
                {draft.options.map((opt, idx) => {
                  const isCorrect = draft.correctAnswers.includes(idx);
                  return (
                    <div key={idx} className="flex items-center gap-2.5">
                      <button
                        type="button"
                        onClick={() => toggleMultiCorrect(idx)}
                        className={`w-6 h-6 flex items-center justify-center rounded-md border-2 flex-shrink-0 cursor-pointer transition-colors ${
                          isCorrect ? 'border-teal-500 bg-teal-500' : 'border-slate-300 hover:border-teal-300'
                        }`}
                      >
                        {isCorrect && <i className="ri-check-line text-white text-xs" />}
                      </button>
                      <input
                        type="text"
                        value={opt}
                        onChange={e => updateOption(idx, e.target.value)}
                        placeholder={`Option ${String.fromCharCode(65 + idx)}`}
                        className={`flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none transition-colors ${
                          isCorrect ? 'border-teal-300 bg-teal-50 focus:border-teal-500' : 'border-slate-200 focus:border-teal-500'
                        }`}
                      />
                      {isCorrect && <span className="text-xs text-teal-600 font-semibold whitespace-nowrap">Correct</span>}
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-slate-400 mt-2">Check every box that's a correct answer (more than one allowed)</p>
            </div>
          )}

          {/* Ordering items (correct order is the order authored here) */}
          {draft.type === 'ordering' && (
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-2">Items * (in the correct order)</label>
              <div className="space-y-2">
                {draft.options.map((opt, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-slate-100 text-slate-600 text-xs font-bold flex-shrink-0">
                      {idx + 1}
                    </span>
                    <input
                      type="text"
                      value={opt}
                      onChange={e => updateOption(idx, e.target.value)}
                      placeholder={`Step ${idx + 1}`}
                      className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-500"
                    />
                    <button
                      type="button"
                      onClick={() => moveOrderingItem(idx, -1)}
                      disabled={idx === 0}
                      className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-teal-600 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <i className="ri-arrow-up-line" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveOrderingItem(idx, 1)}
                      disabled={idx === draft.options.length - 1}
                      className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-teal-600 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <i className="ri-arrow-down-line" />
                    </button>
                    {draft.options.length > 2 && (
                      <button
                        type="button"
                        onClick={() => removeOrderingItem(idx)}
                        className="w-7 h-7 flex items-center justify-center text-rose-400 hover:text-rose-600 cursor-pointer"
                      >
                        <i className="ri-close-line" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={addOrderingItem}
                className="mt-2 text-xs font-semibold text-teal-600 hover:text-teal-700 inline-flex items-center gap-1 cursor-pointer"
              >
                <i className="ri-add-line" /> Add item
              </button>
              <p className="text-xs text-slate-400 mt-2">Students see these shuffled and must arrange them back into this order.</p>
            </div>
          )}

          {/* Fill in the Blank */}
          {draft.type === 'fill-blank' && (
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-2">Accepted Answers * (any one counts as correct)</label>
              <div className="space-y-2">
                {draft.acceptedAnswers.map((ans, idx) => (
                  <div key={idx} className="flex items-center gap-2.5">
                    <input
                      type="text"
                      value={ans}
                      onChange={e => updateAcceptedAnswer(idx, e.target.value)}
                      placeholder="e.g. Paris"
                      className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-500"
                    />
                    {draft.acceptedAnswers.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeAcceptedAnswer(idx)}
                        className="w-7 h-7 flex items-center justify-center text-rose-400 hover:text-rose-600 cursor-pointer"
                      >
                        <i className="ri-close-line" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={addAcceptedAnswer}
                className="mt-2 text-xs font-semibold text-teal-600 hover:text-teal-700 inline-flex items-center gap-1 cursor-pointer"
              >
                <i className="ri-add-line" /> Add another accepted answer
              </button>
              <p className="text-xs text-slate-400 mt-2">Matched case-insensitively, ignoring extra spaces -- list alternate spellings/phrasings here.</p>
            </div>
          )}

          {draft.type === 'discussion' && (
            <div className="p-3.5 bg-purple-50/70 border border-purple-200 rounded-xl">
              <div className="flex items-center gap-2 text-purple-800 font-semibold text-xs mb-1">
                <i className="ri-information-line text-sm text-purple-600" />
                Discussion Question Mode
              </div>
              <p className="text-xs text-purple-700 leading-relaxed">
                There's no single right answer here. Students can share their own reflection, and it's always counted as complete -- nothing to grade. You don't need to add anything below now; you (or the class facilitator) can come back and add a suggested answer or facilitator notes any time later.
              </p>
            </div>
          )}

          {draft.type === 'task' && (
            <div className="p-3.5 bg-teal-50/70 border border-teal-200 rounded-xl">
              <div className="flex items-center gap-2 text-teal-800 font-semibold text-xs mb-1">
                <i className="ri-information-line text-sm text-teal-600" />
                Practical Task Mode
              </div>
              <p className="text-xs text-teal-700 leading-relaxed">
                Students will be prompted to solve this task, write their notes/code, and upload a screenshot or image of their work to complete the lesson checkpoint.
              </p>
            </div>
          )}

          {/* Explanation */}
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1.5">
              {draft.type === 'task' ? 'Solution Guidelines / Grading Tips (Optional)' : draft.type === 'discussion' ? 'Suggested Answer / Facilitator Notes (Optional -- can add later)' : 'Explanation (Optional)'}
            </label>
            <textarea
              value={draft.explanation}
              onChange={e => setDraft(prev => ({ ...prev, explanation: e.target.value }))}
              placeholder={draft.type === 'task' ? 'Provide key points or criteria for a successful task solution...' : draft.type === 'discussion' ? 'Optional: notes for facilitators, or a model answer to reveal after students share their own...' : 'Explain why this is the correct answer...'}
              rows={2}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-500 resize-none"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 p-5 border-t border-slate-100 bg-slate-50/50 rounded-b-2xl">
          <button onClick={onClose} className="px-4 py-2.5 border border-slate-200 text-slate-600 text-sm font-medium rounded-lg cursor-pointer hover:bg-slate-100 whitespace-nowrap">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!draft.text?.trim()}
            className="px-5 py-2.5 bg-teal-600 text-white text-sm font-semibold rounded-lg cursor-pointer hover:bg-teal-700 disabled:opacity-50 whitespace-nowrap shadow-sm"
          >
            {isNew ? 'Add Item' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
